import type { ChildAvailabilityItem } from '@sola/schema/dotcom/children/child.ts';
import type {
  DepartureSubUpdateRequest,
  DepartureSubUpdateResponse,
} from '@sola/schema/dotcom/proxy-api/departures.ts';
import { CommitmentStatusEnum } from '@sola/schema/dotcom/supporters/commitments.ts';
import {
  type GraphQLCommitment,
  type GraphQLDeparture,
  GraphQLSponsorshipChangeState,
  type GraphQLSupporter,
} from '@sola/schema/dotcom/supporters/schema.generated.ts';
import { updateMockSupporter } from '@sola/supporter-sdk/constants.ts';

import {
  MOCK_DEPARTURE_SUB_CANDIDATES,
  MOCK_UNAVAILABLE_REPLACEMENT_CHILD_ID,
} from '../children/mocks.ts';
import { DepartureSubUnavailableError } from './requests.ts';

type MockDepartureSubUpdate = {
  response: DepartureSubUpdateResponse;
  supporter: GraphQLSupporter;
};

const getDeparture = (
  supporter: GraphQLSupporter,
  departedChildGlobalId: string,
): GraphQLDeparture | undefined =>
  supporter.departureSummary.departures.find(
    ({ departedChild }) => departedChild.globalId === departedChildGlobalId,
  );

const getCommitmentForDeparture = (
  commitments: GraphQLCommitment[],
  departure: GraphQLDeparture,
): GraphQLCommitment | undefined => {
  const commitmentInternalId = departure.departedChild.commitmentInternalId;
  return (
    (commitmentInternalId
      ? commitments.find(
          ({ internalId }) => internalId === commitmentInternalId,
        )
      : undefined) ??
    commitments.find(
      ({ participant }) =>
        participant?.globalId === departure.departedChild.globalId,
    )
  );
};

const getPendingSubCommitment = (
  commitments: GraphQLCommitment[],
  departure: GraphQLDeparture,
): GraphQLCommitment | undefined =>
  departure.sub?.pendingCommitmentId
    ? commitments.find(
        ({ internalId }) => internalId === departure.sub?.pendingCommitmentId,
      )
    : undefined;

const getMockReplacementChild = (
  replacementChildGlobalId: string,
): ChildAvailabilityItem | undefined =>
  MOCK_DEPARTURE_SUB_CANDIDATES.find(
    ({ availability }) => availability.globalId === replacementChildGlobalId,
  )?.availability;

const getParticipantFromChild = (
  child: NonNullable<GraphQLDeparture['sub']>['child'] | ChildAvailabilityItem,
  existingParticipant: GraphQLCommitment['participant'],
): GraphQLCommitment['participant'] => {
  if ('name' in child) {
    return {
      ...existingParticipant,
      birthdate:
        child.birthDate?.split(' ')[0] ?? existingParticipant?.birthdate,
      country: child.icp.country,
      gender: child.gender,
      globalId: child.globalId,
      localId: child.localId,
      name: child.name.preferred,
      photoUrl: child.imageUrl,
    };
  }

  return {
    ...existingParticipant,
    globalId: child.globalId,
    name: child.displayName,
    photoUrl: child.imageUrl,
  };
};

const promoteCommitment = ({
  departure,
  departedCommitment,
  pendingSubCommitment,
  replacementChild,
  replacementChildGlobalId,
}: {
  departure: GraphQLDeparture;
  departedCommitment?: GraphQLCommitment;
  pendingSubCommitment?: GraphQLCommitment;
  replacementChild?: ChildAvailabilityItem;
  replacementChildGlobalId?: string;
}): GraphQLCommitment | undefined => {
  const templateCommitment = pendingSubCommitment ?? departedCommitment;
  const child = replacementChild ?? departure.sub?.child;

  if (!templateCommitment || !child) {
    return undefined;
  }

  const isReplacement = !!replacementChildGlobalId;
  let participant: GraphQLCommitment['participant'];

  if (!isReplacement) {
    participant = getParticipantFromChild(
      child,
      templateCommitment.participant,
    );
  } else if (replacementChild) {
    participant = getParticipantFromChild(
      replacementChild,
      templateCommitment.participant,
    );
  } else {
    return undefined;
  }

  return {
    ...templateCommitment,
    dates: { ...templateCommitment.dates, end: null },
    globalId: isReplacement
      ? `mock-${replacementChildGlobalId}`
      : templateCommitment.globalId,
    internalId: isReplacement
      ? `mock-${replacementChildGlobalId}`
      : templateCommitment.internalId,
    participant,
    canGiveGifts: true,
    canMakePayments: true,
    canWrite: true,
    isRecurring: true,
    sponsorshipChangeState: undefined,
    status: CommitmentStatusEnum.Active,
  };
};

export const applyMockDepartureSubUpdate = (
  supporter: GraphQLSupporter,
  departedChildGlobalId: string,
  payload: DepartureSubUpdateRequest,
): MockDepartureSubUpdate => {
  const departure = getDeparture(supporter, departedChildGlobalId);
  const commitments = supporter.commitments ?? [];

  if (!departure?.sub) {
    return {
      response: { success: false },
      supporter,
    };
  }

  const departedCommitment = getCommitmentForDeparture(commitments, departure);
  const pendingSubCommitment = getPendingSubCommitment(commitments, departure);
  const replacementChild = payload.acceptOfferedChild
    ? undefined
    : getMockReplacementChild(payload.replacementChildGlobalId);
  if (!payload.acceptOfferedChild && !replacementChild) {
    return { response: { success: false }, supporter };
  }
  const promotedCommitment = promoteCommitment({
    departure,
    departedCommitment,
    pendingSubCommitment,
    replacementChild,
    replacementChildGlobalId: payload.acceptOfferedChild
      ? undefined
      : payload.replacementChildGlobalId,
  });
  const commitmentsToRemove = new Set(
    [departedCommitment?.internalId, pendingSubCommitment?.internalId].filter(
      (internalId): internalId is string => !!internalId,
    ),
  );
  const closedDepartedCommitment = departedCommitment
    ? {
        ...departedCommitment,
        canGiveGifts: false,
        canMakePayments: false,
        canWrite: false,
        isRecurring: false,
        sponsorshipChangeState: GraphQLSponsorshipChangeState.Transition,
        status: CommitmentStatusEnum.Closed,
      }
    : undefined;
  const nextCommitments = [
    ...commitments.filter(
      ({ internalId }) => !commitmentsToRemove.has(internalId),
    ),
    ...(closedDepartedCommitment ? [closedDepartedCommitment] : []),
    ...(promotedCommitment ? [promotedCommitment] : []),
  ];
  const nextDepartureSummary = {
    ...supporter.departureSummary,
    departures: supporter.departureSummary.departures.map((dptr) => {
      if (dptr.departedChild.globalId !== departedChildGlobalId) {
        return dptr;
      }

      return {
        ...dptr,
        sub: null,
      };
    }),
  };
  const nextSupporter: GraphQLSupporter = {
    ...supporter,
    commitments: nextCommitments,
    departureSummary: nextDepartureSummary,
  };

  return {
    response: {
      departureSummary: nextDepartureSummary,
      success: true,
    },
    supporter: nextSupporter,
  };
};

export const updateMockDepartureSub = (
  departedChildGlobalId: string,
  payload: DepartureSubUpdateRequest,
): DepartureSubUpdateResponse => {
  if (
    !payload.acceptOfferedChild &&
    payload.replacementChildGlobalId === MOCK_UNAVAILABLE_REPLACEMENT_CHILD_ID
  ) {
    throw new DepartureSubUnavailableError({ success: false });
  }

  const updatedSupporter = updateMockSupporter((supporter) => {
    const result = applyMockDepartureSubUpdate(
      supporter,
      departedChildGlobalId,
      payload,
    );

    if (!result.response.success) {
      throw new Error('Mock departure is unavailable.');
    }

    return result.supporter;
  });

  if (!updatedSupporter) {
    throw new Error('Mock supporter is unavailable.');
  }

  return {
    departureSummary: updatedSupporter.departureSummary,
    success: true,
  };
};
